import type { StreakSummary } from '../services/account/statsApi'

// What the streak card says. Kept as a pure function so the wording for every
// situation is easy to test and to tune. The style follows games like Subway
// Surfers and Duolingo: celebrate a milestone, warn (kindly) when a streak is at
// risk, and always say what to do next.

export type StreakTone = 'start' | 'ok' | 'warning' | 'celebrate'

export interface StreakMessage {
  tone: StreakTone
  title: string
  detail: string
  /** A second line about streak freezes, when there is something to say. */
  freezeNote?: string
}

const MILESTONES: ReadonlySet<number> = new Set([3, 7, 14, 30, 50, 100, 200, 365])

const days = (n: number) => `${n} day${n === 1 ? '' : 's'}`

const freezeNoteFor = (streak: StreakSummary): string | undefined => {
  if (streak.savedByFreeze) return 'A streak freeze saved your streak yesterday.'
  if (streak.freezes > 0) {
    return `${streak.freezes} streak freeze${streak.freezes === 1 ? '' : 's'} ready to cover a missed day.`
  }
  return undefined
}

const untilNext = (streak: StreakSummary): string =>
  streak.nextMilestone ? ` ${days(streak.nextMilestone - streak.current)} to ${streak.nextMilestone}.` : ''

export const describeStreak = (streak: StreakSummary): StreakMessage => {
  if (streak.current === 0) {
    return streak.lastPlayed
      ? {
          tone: 'start',
          title: 'Start a new streak',
          detail: `Your best is ${days(streak.longest)}. Play any puzzle today to begin again.`,
        }
      : { tone: 'start', title: 'Start your streak', detail: 'Play any puzzle today to begin.' }
  }

  const freezeNote = freezeNoteFor(streak)

  if (streak.atRisk) {
    return {
      tone: 'warning',
      title: `${streak.current}-day streak at risk`,
      detail: 'Play any puzzle today to keep it going.',
      freezeNote,
    }
  }

  if (MILESTONES.has(streak.current)) {
    return {
      tone: 'celebrate',
      title: `${streak.current}-day streak!`,
      detail: `A milestone. Come back tomorrow to keep it going.${untilNext(streak)}`,
      freezeNote,
    }
  }

  return {
    tone: 'ok',
    title: `${streak.current}-day streak`,
    detail: `Come back tomorrow to keep it going.${untilNext(streak)}`,
    freezeNote,
  }
}
